import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormErrorMessage,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FiCheckSquare } from 'react-icons/fi';
import { Select } from 'chakra-react-select';
import { chakraStyles } from '../common/select/styles';
import { useAddSkillMutation } from '@/app/services/nucleus';
import LoaderOverlay from '../common/LoaderOverlay';
import AlertComponent from '../common/AlertComponent';
import FormRow from '@/features/user/FormRow';

const SkillsModal = ({ isOpen, onClose, onSave, modalityOptions = [] }) => {
  const initialRef = useRef(null);
  const finalRef = useRef(null);

  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [modalities, setModalities] = useState([]);
  const [descriptionError, setDescriptionError] = useState(false);
  const [categoryError, setCategoryError] = useState(false);

  const [addSkill, { isLoading: isSubmitting, isError, error, reset }] = useAddSkillMutation();

  useEffect(() => {
    if (!isOpen) {
      setDescription('');
      setCategory('');
      setModalities([]);
      setDescriptionError(false);
      setCategoryError(false);
      reset();
    }
  }, [isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    let hasError = false;
    if (!description.trim()) {
      setDescriptionError(true);
      hasError = true;
    }
    if (!category.trim()) {
      setCategoryError(true);
      hasError = true;
    }
    if (hasError) return;

    addSkill({
      description: description.trim(),
      category: category.trim(),
      modality_ids: modalities.map((m) => m.value),
    })
      .unwrap()
      .then(() => {
        onSave();
        onClose();
      })
      .catch((e) => console.error('Error adding skill', e));
  };

  return (
    <Modal initialFocusRef={initialRef} finalFocusRef={finalRef} isOpen={isOpen} onClose={onClose} isCentered size="2xl" closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <form id="add_skill" onSubmit={handleSubmit}>
          <ModalHeader>
            <HStack>
              <FiCheckSquare />
              <Text>Add Skill</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4} position="relative">
              <LoaderOverlay loading={isSubmitting} />
              {isError && <AlertComponent status="error" title="Error adding skill" description={error?.data?.detail ?? 'An error occurred'} />}
              <Alert status="info" borderRadius="md" fontSize="sm">
                <AlertIcon />
                Skills cannot be edited after creation and modality tags are fixed at create time. To correct a skill, delete it and create a new one.
              </Alert>
              <FormControl isInvalid={descriptionError} aria-label="description-control">
                <FormRow id="description" label="Description" templateColumns="110px auto" gap={0} width="100%">
                  <Input
                    ref={initialRef}
                    id="description"
                    focusBorderColor="brand.700"
                    value={description}
                    onChange={(e) => {
                      setDescription(e.target.value);
                      setDescriptionError(false);
                    }}
                  />
                </FormRow>
                <FormErrorMessage ml="110px">Description is required.</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={categoryError} aria-label="category-control">
                <FormRow id="category" label="Category" templateColumns="110px auto" gap={0} width="100%">
                  <Input
                    id="category"
                    focusBorderColor="brand.700"
                    value={category}
                    onChange={(e) => {
                      setCategory(e.target.value);
                      setCategoryError(false);
                    }}
                  />
                </FormRow>
                <FormErrorMessage ml="110px">Category is required.</FormErrorMessage>
              </FormControl>
              <FormControl aria-label="modalities-control">
                <FormRow id="modalities" label="Modalities" templateColumns="110px auto" gap={0} width="100%">
                  <Select
                    id="modalities"
                    aria-label="modalities"
                    chakraStyles={chakraStyles}
                    focusBorderColor="brand.700"
                    options={modalityOptions}
                    value={modalities}
                    onChange={(opts) => setModalities(opts || [])}
                    isMulti
                    isClearable
                    isSearchable
                    colorScheme="blue"
                    placeholder="Select modalities"
                  />
                </FormRow>
              </FormControl>
              <HStack width="100%" py={3} justifyContent="end">
                <Button onClick={onClose}>Cancel</Button>
                <Button type="submit" variant="purple">
                  Submit
                </Button>
              </HStack>
            </VStack>
          </ModalBody>
        </form>
      </ModalContent>
    </Modal>
  );
};

SkillsModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  modalityOptions: PropTypes.array,
};

export default SkillsModal;
